/**
 * JSON-serialisable plaintext envelope for the encrypted-local-backup file.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BackupBundle.swift` in role — the
 * cipher layer (`BackupCipher.ts`) is bytes-in / bytes-out and knows
 * nothing about the structure inside the ciphertext. This layer is the
 * plaintext envelope the cipher seals.
 *
 * THREAT MODEL
 *   - The envelope itself is INSIDE the AES-GCM ciphertext, so its
 *     confidentiality + integrity are inherited from the seal layer.
 *     This layer's only responsibilities are:
 *       (a) versioning the plaintext schema so a future field-shape
 *           change doesn't break already-generated backups, and
 *       (b) producing a deterministic byte serialisation so the cipher
 *           has stable inputs.
 *   - The `contents` field is opaque to this layer — the caller (snapshot
 *     serialiser, not yet wired) fills it with whatever JSON-shaped local
 *     state should round-trip through restore. This layer does NOT
 *     interpret it; it only ferries the bytes.
 *
 * Service-Worker importability guarantee:
 *   Pure JSON + TextEncoder. No window, no document, no DOM-only APIs.
 */

/**
 * Versioned wrapper for the plaintext payload. The `schemaVersion` field
 * lets the restore path dispatch on the shape — a v2 bundle adds new
 * required fields and the parser refuses to read it on a v1-only client.
 */
export interface BundleInput {
  /** Schema discriminator. Currently `1`. */
  readonly schemaVersion: 1;
  /** Unix seconds at envelope creation. Surfaced in the file-listing UI. */
  readonly exportedAt: number;
  /** Caller-defined JSON-shaped payload. This layer does not interpret it. */
  readonly contents: unknown;
}

/** Result of `decodeBundle`. Same shape as `BundleInput`. */
export interface DecodedBundle {
  readonly schemaVersion: number;
  readonly exportedAt: number;
  readonly contents: unknown;
}

/**
 * Serialise a bundle to UTF-8 bytes ready for `BackupCipher.seal`. Stable
 * key ordering so the cipher's input bytes are reproducible across two
 * encodes of the same logical input (useful for tests + cross-platform
 * comparison).
 */
export function encodeBundle(input: BundleInput): Uint8Array {
  // The top-level keys are emitted in a fixed order so the serialised
  // bytes are stable across host JS engines.
  const json =
    `{"contents":${JSON.stringify(input.contents)},` +
    `"exportedAt":${JSON.stringify(input.exportedAt)},` +
    `"schemaVersion":${JSON.stringify(input.schemaVersion)}}`;
  return new TextEncoder().encode(json);
}

/**
 * Decode UTF-8 bytes back into the bundle envelope. Returns the typed shape
 * with `contents` left as `unknown` — the caller is responsible for shape-
 * checking before interpreting (the cipher layer does not know what's
 * inside the bundle).
 *
 * @throws Error if the JSON is malformed or the required fields are missing.
 */
export function decodeBundle(bytes: Uint8Array): DecodedBundle {
  const dec = new TextDecoder("utf-8", { fatal: true });
  let text: string;
  try {
    text = dec.decode(bytes);
  } catch (e) {
    throw new Error("BackupBundle: non-utf8 bytes");
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    throw new Error("BackupBundle: json decode failed");
  }
  if (typeof raw !== "object" || raw === null) {
    throw new Error("BackupBundle: not an object");
  }
  const r = raw as Record<string, unknown>;
  if (typeof r.schemaVersion !== "number") {
    throw new Error("BackupBundle: schemaVersion missing or not a number");
  }
  if (typeof r.exportedAt !== "number") {
    throw new Error("BackupBundle: exportedAt missing or not a number");
  }
  return {
    schemaVersion: r.schemaVersion,
    exportedAt: r.exportedAt,
    contents: r.contents,
  };
}
